import { useState, useEffect, useRef } from 'react';
import { B, radius } from '../../constants/brand';
import { fmt } from '../../utils/format';
import { api } from '../../api/client';
import { collectEntriesFromInputFiles, collectEntriesFromDataTransfer } from '../../utils/docFolders';
import { useDocumentFolders } from '../../hooks/useDocumentFolders';
import { useFolderPlan } from '../../hooks/useFolderPlan';
import { FolderPicker } from './FolderPicker';
import { NewOfficeFileModal } from './NewOfficeFileModal';
import { FolderPlanModal } from './FolderPlanModal';
import { downloadDocumentsZip, describeBundle } from '../../utils/exportDownload';
import { openDocument, isPdf, isOffice } from '../../utils/docViewer';
import { DocViewerModal } from './DocViewerModal';
import { DocumentTypeChips, DocumentTagModal, SplitDocumentModal } from './DocumentTypeChips';
import { intakeStatusLabel, intakeIsBusy } from '../../utils/documentIntakeStatus';
import { Badge, useToast, Icon, Modal } from '../ui';

// ─── Helpers ────────────────────────────────────────────────────────────────

// Returns unique immediate child folder names + files that live exactly at currentPath.
//
// `knownFolders` is the deal's live folder list (templates + Harvi-created +
// what's in SharePoint). Folding it in is what makes an EMPTY folder browsable:
// derived purely from documents, a folder someone just created — here or in
// SharePoint — would be invisible until the first file landed in it.
function getChildren(docs, currentPath, knownFolders = []) {
  const folderSet = new Set();
  const files = [];

  const addChildOf = (fp) => {
    if (currentPath === '') {
      if (fp) folderSet.add(fp.split('/')[0]);
    } else if (fp.startsWith(currentPath + '/')) {
      folderSet.add(fp.slice(currentPath.length + 1).split('/')[0]);
    }
  };

  for (const doc of docs) {
    const fp = doc.folderPath || '';
    if (currentPath === '' ? !fp : fp === currentPath) files.push(doc);
    else addChildOf(fp);
  }
  for (const path of knownFolders) addChildOf(path || '');

  return {
    folders: Array.from(folderSet).sort((a, b) => a.localeCompare(b)),
    files:   files.slice().sort((a, b) => new Date(b.uploadedUTC) - new Date(a.uploadedUTC)),
  };
}

// Count docs at or below a path (for folder badges).
function countUnder(docs, path) {
  return docs.filter(d => {
    const fp = d.folderPath || '';
    return path === '' ? true : fp === path || fp.startsWith(path + '/');
  }).length;
}

// ─── File-type chip ─────────────────────────────────────────────────────────

const EXT_COLOR = {
  pdf: '#c0392b', doc: '#2b5fad', docx: '#2b5fad',
  xls: '#1d6f42', xlsx: '#1d6f42', ppt: '#d04423', pptx: '#d04423',
  png: '#7d3c98', jpg: '#7d3c98', jpeg: '#7d3c98',
};

const FileChip = ({ fileName }) => {
  const ext = (fileName || '').split('.').pop().toLowerCase();
  const bg  = EXT_COLOR[ext] || B.textMd;
  return (
    <span style={{ fontSize: 9, fontWeight: 700, color: '#fff', background: bg,
      borderRadius: 3, padding: '2px 5px', letterSpacing: 0.4, textTransform: 'uppercase',
      flexShrink: 0 }}>
      {ext.slice(0, 4) || 'FILE'}
    </span>
  );
};

// ─── OCR + SP status helpers ─────────────────────────────────────────────────

// The API returns `dbo.Documents` rows raw and `toCamel` (shared/src/apiCore.ts)
// lowercases only the FIRST character, so `IsOCRComplete` arrives as
// `isOCRComplete` — NOT `isOcrComplete`. Reading the latter was always
// `undefined`, so this badge could never say "Indexed": it rendered the amber
// "Uploaded" regardless, including for rows whose OcrText and AI summary are
// sitting in the database. Every other consumer (shared/src/models.ts,
// pages/Documents.jsx, mobile/app/documents.tsx) already spells it
// `isOCRComplete`; this was the one outlier, which is why the Documents page
// reported "OCR indexed" for a file this panel called "Uploaded".
//
// NOTE THE NARROW BLAST RADIUS, because it is what makes the symptom
// diagnostic: these two badges render ONLY when `intakeStatusLabel(doc)` is
// null (see the Status cell below), and that is null only for a row with no
// `AiIntakeStatus` — one that predates the intake pipeline or never entered it.
// An intake-tracked document shows an IntakeChip instead and never reaches
// here. So a row reading "Uploaded" is not merely un-indexed: it is a document
// document intake has never touched.
const OcrBadge = ({ doc }) => (
  <Badge color={
    doc.isOCRComplete ? B.green
    : (doc.ocrError && doc.ocrError !== 'Document Intelligence not configured') ? B.red
    : B.amber
  }>
    {doc.isOCRComplete ? 'Indexed'
      : (doc.ocrError && doc.ocrError !== 'Document Intelligence not configured') ? 'Failed'
      : 'Uploaded'}
  </Badge>
);

const SpStatus = ({ doc }) => {
  if (doc.sharePointSyncStatus === 'synced')
    return <span title={`SharePoint: ${doc.sharePointWebUrl || 'synced'}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: B.green }}>SP <Icon name="check" size={12} /></span>;
  if (doc.sharePointSyncStatus === 'pending')
    return <span title="SharePoint sync in progress" style={{ fontSize: 11, color: B.textLt }}>SP …</span>;
  if (doc.sharePointSyncStatus === 'error')
    return <span title={`SharePoint sync failed: ${doc.sharePointSyncError}`} style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: B.red }}>SP <Icon name="close" size={12} /></span>;
  // ANYTHING ELSE IS THE FOURTH STATE, AND IT USED TO RENDER NOTHING AT ALL.
  // A NULL status is a document that was never queued for sync — so it has no
  // SharePointItemID, so `documents/{id}/edit-url` 409s, so docViewer.js falls
  // through to the public Office viewer and the file opens READ-ONLY. That is
  // indistinguishable from a healthy row when the badge is absent, which is how
  // a whole folder of un-editable files reads as fine at a glance.
  // It is deliberately NOT red: nothing failed. But it needs a human, because
  // none of the three recovery paths can see it — `documents/sharepoint/failed`
  // and `retry-failed` both select `'error'`, and documentSharePointReconciler
  // selects `'pending'`. `POST /api/sharepoint/retry-sync/{id}` is the way out:
  // it ignores the current status, sets `'pending'` and syncs.
  return (
    <span
      title={doc.sharePointSyncStatus
        ? `SharePoint: unrecognised sync status "${doc.sharePointSyncStatus}"`
        : 'Not in SharePoint — this document was never queued for sync, so it opens read-only. An admin can start it with Retry sync.'}
      style={{ display: 'inline-flex', alignItems: 'center', gap: 3, fontSize: 11, color: B.amber }}
    >
      SP <Icon name="warning" size={12} />
    </span>
  );
};

// ─── Background-processing status ────────────────────────────────────────────
//
// After the upload, everything else happens without the user: the document is
// read, filed, and put in SharePoint. THIS ROW IS THE ONLY PLACE ANY OF THAT IS
// REPORTED — no toast, no notification, no modal. A failure is a quiet red line
// on the file it belongs to, where the person who cares about it is looking.
const INTAKE_TONE = { busy: B.textLt, ok: B.green, warn: B.amber, error: B.red };

const IntakeChip = ({ status }) => (
  <span
    title={[status.text, status.detail].filter(Boolean).join(' — ')}
    style={{ fontSize: 11, color: INTAKE_TONE[status.tone] || B.textLt, whiteSpace: 'nowrap' }}>
    {status.busy && <span style={{ marginRight: 3 }}>●</span>}{status.short}
  </span>
);

// The full sentence, under the file name. Only rendered while something is
// happening or when something went wrong — a document that filed cleanly says
// nothing here, because there is nothing the user needs to do.
const IntakeLine = ({ status }) => {
  if (!status || status.tone === 'ok') return null;
  return (
    <div style={{ fontSize: 11, color: INTAKE_TONE[status.tone] || B.textLt, marginTop: 2 }}>
      {status.text}
      {status.detail && (
        <span style={{ color: B.textLt }}> — {status.detail}</span>
      )}
    </div>
  );
};

// ─── Grid layout constants ───────────────────────────────────────────────────

const COL = '3fr 120px 1fr 90px 110px 80px'; // Name | Modified | By | Size | Status | Actions

const TH = ({ children, style }) => (
  <div style={{ fontSize: 11, fontWeight: 700, color: B.textMd, letterSpacing: 0.7, ...style }}>{children}</div>
);

// ─── Main component ──────────────────────────────────────────────────────────

// `includeDealLevel` mirrors how the caller filtered `docs`: in the unified deal
// view the panel shows this detail's documents PLUS the deal-level ones, so the
// "Download all" bundle has to be scoped the same way.
export const DocumentsPanel = ({ dealId, dealDetailId, detailType, combinedFolders, includeDealLevel = false, docs: initialDocs, onRefresh }) => {
  const { toast } = useToast();
  const [docs,        setDocs]        = useState(initialDocs || []);
  const [navPath,     setNavPath]     = useState('');        // current folder path in the browser
  const [search,      setSearch]      = useState('');
  const [showUpload,  setShowUpload]  = useState(false);
  const [showNewFile, setShowNewFile] = useState(false);  // blank Word/Excel/PowerPoint
  const [dragging,    setDragging]    = useState(false);
  const [uploading,   setUploading]   = useState(false);
  const [uploadErr,   setUploadErr]   = useState(null);
  const [deleting,    setDeleting]    = useState(null);
  const [downloading, setDownloading] = useState(null);
  const [viewing,     setViewing]     = useState(null);
  const [viewerState, setViewerState] = useState(null);
  const [folderPath,  setFolderPath]  = useState('');
  const [taxYear,     setTaxYear]     = useState('');
  const [hovered,         setHovered]         = useState(null);
  const [uploadProgress,  setUploadProgress]  = useState(null); // { done, total } | null
  const [staged,          setStaged]          = useState([]);   // Array<{ file, relativePath, key }>
  const [tagDocId,        setTagDocId]        = useState(null);  // doc whose types are being edited
  const [splitDocId,      setSplitDocId]      = useState(null);  // doc being split
  const [bundling,        setBundling]        = useState(false);  // "Download all" in flight
  const [newFolderName,   setNewFolderName]   = useState(null);  // string = the "new folder" form is open
  const [renamingFolder,  setRenamingFolder]  = useState(null);  // { path, value } while renaming
  const [folderBusy,      setFolderBusy]      = useState(false);
  const [showPlan,        setShowPlan]        = useState(false);  // folder-plan review modal
  const [moveDoc,         setMoveDoc]         = useState(null);   // { doc, target } while moving one file
  const [moving,          setMoving]          = useState(false);
  const fileRef   = useRef();
  const folderRef = useRef();

  useEffect(() => { setDocs(initialDocs || []); }, [initialDocs]);

  // ── Poll while documents are still being processed in the background ──
  //
  // The upload returns as soon as the bytes are stored; reading, filing and the
  // SharePoint upload happen after it. Refresh the list while any row is still
  // moving so the user watches it settle instead of having to reload — bounded,
  // so a stuck document can't leave the page polling forever (the server's own
  // sweep resolves those to a real status within a couple of minutes anyway).
  const busyCount = docs.filter(intakeIsBusy).length;
  useEffect(() => {
    if (!busyCount || !onRefresh) return undefined;
    let ticks = 0;
    const id = setInterval(() => {
      ticks += 1;
      if (ticks > 40) { clearInterval(id); return; }   // ~3 min at 5s
      onRefresh();
    }, 5000);
    return () => clearInterval(id);
  }, [busyCount, onRefresh]);

  // ── The deal's LIVE folder structure ──
  // Merged server-side from the deal's Claude-generated plan, folders created in
  // Harvi, folders that actually exist under the deal in SharePoint, and the
  // ones its documents already use — so the picker and the browser both reflect
  // what's really there rather than a list baked into the app.
  const {
    folders: liveFolders, tree: folderTree, loading: foldersLoading, error: foldersError,
    sharePointAvailable, createFolder, renameFolder, deleteFolder, refresh: refreshFolders,
  } = useDocumentFolders({ dealId, dealDetailId, detailType });

  // ── The structure Claude derives from this deal's documents ──
  // `auto` generates it once, unprompted, for a deal that has documents but no
  // plan — reading the documents has no side effects. APPLYING the plan
  // (creating folders, moving files in SharePoint) is never automatic: that
  // happens only from the review modal, on the files the user ticked.
  const folderPlan = useFolderPlan({ dealId, dealDetailId, auto: true });

  const leafFolders  = liveFolders;
  const selectedDef  = leafFolders.find(f => f.path === folderPath);
  const requiresYear = selectedDef?.requiresYear || false;
  const effectiveFolderPath = requiresYear && taxYear ? `${folderPath}/${taxYear}` : folderPath;
  const knownFolderPaths = liveFolders.map(f => f.path);

  // How many files the current plan would relocate — surfaced on the button so
  // pending reorganisation is visible without opening the modal.
  const planMoveCount = folderPlan.data?.status === 'ready'
    ? (folderPlan.data.stats?.moves || 0)
    : 0;

  // ── Folder browser (no search) ──
  const { folders: childFolders, files: childFiles } = getChildren(docs, navPath, knownFolderPaths);

  // ── Search results (flat, across all docs) ──
  const searchResults = search
    ? docs.filter(d =>
        d.fileName?.toLowerCase().includes(search.toLowerCase()) ||
        d.folderPath?.toLowerCase().includes(search.toLowerCase())
      ).slice().sort((a, b) => new Date(b.uploadedUTC) - new Date(a.uploadedUTC))
    : null;

  // ── Breadcrumb segments ──
  const crumbs = navPath ? navPath.split('/') : [];

  // ── Upload modal helpers ──
  const openUpload = () => {
    // Pre-select the current navPath if it's a valid leaf folder
    const matchedLeaf = leafFolders.find(f => f.path === navPath);
    setFolderPath(matchedLeaf ? navPath : '');
    setTaxYear('');
    setUploadErr(null);
    setStaged([]);
    setShowUpload(true);
  };
  const closeUpload = () => { setShowUpload(false); setUploadErr(null); setFolderPath(''); setTaxYear(''); setStaged([]); };

  // Accumulate selected/dropped entries into the staging list (dedupe by path + name + size)
  // so multiple files AND folders can be gathered from any input method, then uploaded together.
  const addEntries = (entries) => {
    if (!entries || entries.length === 0) return;
    setUploadErr(null);
    setStaged(prev => {
      const seen = new Set(prev.map(e => e.key));
      const next = prev.slice();
      for (const { file, relativePath } of entries) {
        const key = `${relativePath || ''}\x00${file.name}\x00${file.size}`;
        if (seen.has(key)) continue;
        seen.add(key);
        next.push({ file, relativePath: relativePath || '', key });
      }
      return next;
    });
  };
  const removeStaged = (key) => setStaged(prev => prev.filter(e => e.key !== key));

  const uploadStaged = async () => {
    if (staged.length === 0) { setUploadErr('Add at least one file or folder to upload.'); return; }
    // Picking a folder is optional: anything uploaded without one is auto-filed
    // server-side. The tax year is still required, but only when the user has
    // actually chosen a folder that asks for one.
    if (requiresYear && !taxYear) { setUploadErr('Please enter the tax year for Property Taxes documents.'); return; }
    setUploading(true); setUploadErr(null);
    setUploadProgress({ done: 0, total: staged.length });
    try {
      // Documents that arrive without a folder are auto-filed. confirm-upload
      // does that per file for any client, but a batch here would then cost one
      // Claude call per file — so we defer and make ONE batched call below.
      const unfiled = [];
      // A file the server refuses as incomplete is ONE file's problem, not the
      // batch's — see the rejected-file handling below.
      const rejected = [];
      for (let i = 0; i < staged.length; i++) {
        const { file, relativePath, key } = staged[i];
        const fileFolderPath = [effectiveFolderPath, relativePath].filter(Boolean).join('/') || null;
        const result = await api.post('document-upload-url', {
          fileName:     file.name,
          fileType:     file.name.split('.').pop().toLowerCase(),
          dealId:       dealId       || null,
          dealDetailId: dealDetailId || null,
          description:  '',
          folderPath:   fileFolderPath,
        });
        if (!result.sasUrl) throw new Error('No SAS URL returned from API');
        const putRes = await fetch(result.sasUrl, {
          method:  'PUT',
          headers: { 'x-ms-blob-type': 'BlockBlob', 'Content-Type': file.type || 'application/octet-stream' },
          body:    file,
        });
        if (!putRes.ok) throw new Error(`Blob upload failed: ${putRes.status}`);
        try {
          await api.post(`documents/${result.documentId}/confirm-upload`, {
            fileSizeBytes: file.size,
            versionId:     result.versionId,
            // This client always follows up with the batched call below, so it
            // defers every file that needs a folder — however many there are.
            // (Tying this to staged.length would strand the lone loose file in a
            // batch whose other files came in under a folder of their own.)
            deferAutoFile: !fileFolderPath,
          });
        } catch (e) {
          // The server verifies that what landed in Blob Storage is what the
          // browser said it was sending, and discards the document when it is
          // not. That is a fact about THIS file — usually one held open by
          // another program and re-saved mid-read — so the other files in the
          // batch are unaffected and must not be abandoned with it. Anything
          // else (auth, storage, an outage) still aborts, because it will
          // repeat identically on every remaining file.
          if (e?.body?.discarded) {
            rejected.push({ key, name: file.name, reason: e.message });
            // Still count it: the bar tracks files ATTEMPTED, and freezing it
            // on a rejected file reads as a hung upload.
            setUploadProgress({ done: i + 1, total: staged.length });
            continue;
          }
          throw e;
        }
        if (!fileFolderPath) unfiled.push(result.documentId);
        setUploadProgress({ done: i + 1, total: staged.length });
      }

      // One batched auto-file for everything that came in without a folder.
      // Best-effort: the files are uploaded and safe either way, they'd just be
      // sitting at the deal root — which is where they used to stay for good.
      if (unfiled.length) {
        try {
          const res = await api.post('documents/auto-file', {
            dealId: dealId || null, dealDetailId: dealDetailId || null,
            documentIds: unfiled,
          }, { timeout: 120000 });
          const filed = res?.filed?.length || 0;
          if (filed) toast.success(`Filed ${filed} document${filed === 1 ? '' : 's'} into folders.`);
        } catch (e) {
          toast.error(`Uploaded, but automatic foldering failed: ${e.message}`);
        }
      }

      // Everything that landed is refreshed either way. When some files were
      // discarded the modal STAYS OPEN, holding exactly those files and saying
      // why — closing it would report a clean upload for a batch that was not
      // one, and the person would have to work out which files to find again.
      // The staged list is narrowed to the rejected ones so pressing Upload a
      // second time retries them without duplicating the ones that succeeded.
      if (rejected.length) {
        const keys = new Set(rejected.map(r => r.key));
        setStaged(prev => prev.filter(e => keys.has(e.key)));
        setUploadErr(rejected.length === 1
          ? rejected[0].reason
          : `${rejected.length} files did not upload completely and were discarded: `
            + rejected.map(r => r.name).join(', ')
            + '. Close them in any program that has them open, then try again.');
        if (onRefresh) await onRefresh();
        return;
      }

      closeUpload();
      if (onRefresh) await onRefresh();
    } catch (e) { setUploadErr(e.message); }
    finally { setUploading(false); setUploadProgress(null); }
  };

  // ── Folder management (Harvi and SharePoint together) ──
  // Every one of these hits SharePoint as well as Harvi — a folder that only
  // exists on one side is the problem this feature set out to remove.
  const submitNewFolder = async () => {
    const name = (newFolderName || '').trim();
    if (!name) { setNewFolderName(null); return; }
    setFolderBusy(true);
    try {
      const created = await createFolder(navPath ? `${navPath}/${name}` : name);
      setNewFolderName(null);
      toast.success(`Created "${created?.path || name}"`);
    } catch (e) { toast.error(e.message); }
    finally { setFolderBusy(false); }
  };

  const submitRename = async () => {
    const { path, value } = renamingFolder || {};
    const name = (value || '').trim();
    if (!path || !name) { setRenamingFolder(null); return; }
    const parent  = path.includes('/') ? path.slice(0, path.lastIndexOf('/')) : '';
    const newPath = parent ? `${parent}/${name}` : name;
    if (newPath === path) { setRenamingFolder(null); return; }
    setFolderBusy(true);
    try {
      const res = await renameFolder(path, newPath);
      setRenamingFolder(null);
      // The documents' folderPath changed server-side — reload so the browser
      // isn't showing the old tree.
      if (onRefresh) await onRefresh();
      toast.success(`Renamed to "${newPath}"${res?.movedDocuments ? ` (${res.movedDocuments} document${res.movedDocuments === 1 ? '' : 's'} moved)` : ''}`);
    } catch (e) { toast.error(e.message); }
    finally { setFolderBusy(false); }
  };

  const removeFolder = async (path) => {
    if (!window.confirm(`Remove the folder "${path}"? It must be empty.`)) return;
    setFolderBusy(true);
    try {
      await deleteFolder(path);
      toast.success(`Removed "${path}"`);
    } catch (e) { toast.error(e.message); }
    finally { setFolderBusy(false); }
  };

  // Move one document to another folder. The server records the move as a
  // correction when the folder being left was the AI's choice, which is what
  // teaches the folder picker (Settings → AI Connections → AI feedback).
  const confirmMove = async () => {
    if (!moveDoc) return;
    setMoving(true);
    try {
      const r = await api.put(`documents/${moveDoc.doc.documentID}/folder`, { folderPath: moveDoc.target || '' });
      setDocs(p => p.map(d => (d.documentID === moveDoc.doc.documentID ? { ...d, folderPath: r.folderPath || '' } : d)));
      toast.success(r.moved ? `Moved to ${r.folderPath || 'the deal root'}` : 'Already in that folder.');
      setMoveDoc(null);
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(e.message || 'Could not move the document.'); }
    finally { setMoving(false); }
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm('Remove this document? It is removed from Harvus and from SharePoint; the stored file is kept, so an administrator can undo this.')) return;
    setDeleting(docId);
    try {
      await api.del(`documents/${docId}`);
      setDocs(p => p.filter(d => d.documentID !== docId));
      if (onRefresh) onRefresh();
    } catch (e) { toast.error(e.message); }
    finally { setDeleting(null); }
  };

  const downloadDoc = async (doc) => {
    setDownloading(doc.documentID);
    try {
      const { sasUrl, fileName } = await api.get(`documents/${doc.documentID}/download-url`);
      const a = document.createElement('a');
      a.href = sasUrl; a.download = fileName; a.click();
    } catch (e) { toast.error(e.message); }
    finally { setDownloading(null); }
  };

  // ── Download every document in view as one .zip ──
  // Follows what the user is looking at: the search results if they're
  // searching, the current folder subtree if they've navigated into one, and
  // otherwise the whole deal / deal-detail scope.
  const bundleScope = search
    ? { documentIds: (searchResults || []).map(d => d.documentID), label: `Download results (${(searchResults || []).length})` }
    : navPath
      ? { folderPath: navPath, label: 'Download folder' }
      : { label: 'Download all' };

  const bundleCount = search ? (searchResults || []).length : countUnder(docs, navPath);

  const downloadAll = async () => {
    if (!bundleCount) { toast.info('There are no documents here to download.'); return; }
    setBundling(true);
    try {
      const res = await downloadDocumentsZip({ dealId, dealDetailId, includeDealLevel, ...bundleScope });
      if (res?.skipped?.length) toast.info(describeBundle(res));
    } catch (e) { toast.error('Download failed: ' + e.message); }
    finally { setBundling(false); }
  };

  // ── Document-type tags ──
  const setLocalTypes = (docId, types) =>
    setDocs(prev => prev.map(d => d.documentID === docId ? { ...d, types } : d));
  const applyType = async (docId, code) => {
    try { const r = await api.put(`documents/${docId}/types`, { apply: [code] }); setLocalTypes(docId, r.types); }
    catch (e) { toast.error(e.message); }
  };
  const removeType = async (docId, code) => {
    try { const r = await api.put(`documents/${docId}/types`, { remove: [code] }); setLocalTypes(docId, r.types); }
    catch (e) { toast.error(e.message); }
  };
  const typesEnabled = (doc) => detailType === 'R/E' || (doc.types || []).length > 0;

  // ── Shared file row renderer ──
  const FileRow = ({ doc, idx, total }) => (
    <div
      onMouseEnter={() => setHovered(doc.documentID)}
      onMouseLeave={() => setHovered(null)}
      style={{ display: 'grid', gridTemplateColumns: COL, padding: '10px 20px', gap: 8,
        borderBottom: idx < total - 1 ? `1px solid ${B.border}` : 'none',
        alignItems: 'center', background: hovered === doc.documentID ? B.surfaceMuted : B.white,
        transition: 'background 0.1s' }}>

      {/* Name */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
        <FileChip fileName={doc.fileName} />
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 500, color: B.navy,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {doc.fileName}
          </div>
          {searchResults && doc.folderPath && (
            <div style={{ fontSize: 11, color: B.textLt, marginTop: 1, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="folder" size={11} /> {doc.folderPath}</div>
          )}
          <IntakeLine status={intakeStatusLabel(doc)} />
          <DocumentTypeChips
            doc={doc}
            enabled={typesEnabled(doc)}
            onApply={applyType}
            onRemove={removeType}
            onEdit={() => setTagDocId(doc.documentID)}
            onSplit={() => setSplitDocId(doc.documentID)}
          />
        </div>
      </div>

      {/* Modified */}
      <div style={{ fontSize: 12, color: B.textMd }}>{fmt.date(doc.uploadedUTC)}</div>

      {/* By */}
      <div style={{ fontSize: 12, color: B.textMd, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {doc.uploadedByName || '—'}
      </div>

      {/* Size */}
      <div style={{ fontSize: 12, color: B.textMd }}>
        {doc.fileSizeBytes ? fmt.fileSize(doc.fileSizeBytes) : '—'}
      </div>

      {/* Status — the human line when intake is tracking this document, the raw
          OCR/SharePoint badges otherwise (a legacy row, or a pre-157 schema). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
        {intakeStatusLabel(doc)
          ? <IntakeChip status={intakeStatusLabel(doc)} />
          : <><OcrBadge doc={doc} /><SpStatus doc={doc} /></>}
      </div>

      {/* Actions */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 2,
        opacity: hovered === doc.documentID ? 1 : 0, transition: 'opacity 0.1s' }}>
        <button
          onClick={() => { setViewing(doc.documentID); openDocument(doc, api, setViewerState, toast.error).finally(() => setViewing(null)); }}
          disabled={viewing === doc.documentID}
          style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12,
            padding: '2px 6px', color: B.navy, fontFamily: 'inherit', whiteSpace: 'nowrap' }}>
          {viewing === doc.documentID ? '…' : isPdf(doc.fileName) ? 'View' : isOffice(doc.fileName) ? 'Office' : 'View'}
        </button>
        <button onClick={() => downloadDoc(doc)} disabled={downloading === doc.documentID}
          title="Download"
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex',
            padding: '4px', color: B.navy, opacity: 0.75 }}>
          {downloading === doc.documentID ? '…' : <Icon name="download" size={15} />}
        </button>
        <button onClick={() => setMoveDoc({ doc, target: doc.folderPath || '' })}
          title="Move to folder"
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex',
            padding: '4px', color: B.navy, opacity: 0.75 }}>
          <Icon name="folder" size={15} />
        </button>
        <button onClick={() => deleteDoc(doc.documentID)} disabled={deleting === doc.documentID}
          title="Delete"
          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex',
            padding: '4px', color: B.red, opacity: 0.65 }}>
          {deleting === doc.documentID ? '…' : <Icon name="trash" size={15} />}
        </button>
      </div>
    </div>
  );

  const totalVisible = childFolders.length + childFiles.length;

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <div style={{ fontSize: 20, fontWeight: 700,
          color: B.text, letterSpacing: -0.3 }}>
          Documents
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <button onClick={downloadAll} disabled={bundling || !bundleCount}
            title={bundleCount ? `Download ${bundleCount} document${bundleCount === 1 ? '' : 's'} as a .zip` : 'Nothing to download'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: B.white,
              color: (bundling || !bundleCount) ? B.textLt : B.navy, border: `1px solid ${B.border}`, borderRadius: 8,
              padding: '10px 16px', fontSize: 14, fontWeight: 600,
              cursor: (bundling || !bundleCount) ? 'default' : 'pointer', fontFamily: 'inherit', letterSpacing: 0.2 }}>
            <Icon name="archive" size={15} /> {bundling ? 'Preparing…' : bundleScope.label}
          </button>
          <button onClick={() => setShowPlan(true)} disabled={folderPlan.applying}
            title="Let Claude design this deal's folder structure from its documents, then organise SharePoint to match"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: B.white,
              color: B.navy, border: `1px solid ${B.border}`, borderRadius: 8,
              padding: '10px 16px', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.2 }}>
            <Icon name="sparkles" size={15} />
            {folderPlan.generating ? 'Organizing…' : 'Organize folders'}
            {planMoveCount > 0 && (
              <span style={{ fontSize: 11, fontWeight: 700, background: B.tealLt, color: B.navyDk,
                borderRadius: 9, padding: '1px 6px' }}>{planMoveCount}</span>
            )}
          </button>
          <button onClick={() => setNewFolderName('')} disabled={folderBusy}
            title={navPath ? `Create a folder inside "${navPath}"` : 'Create a top-level folder for this deal'}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: B.white,
              color: folderBusy ? B.textLt : B.navy, border: `1px solid ${B.border}`, borderRadius: 8,
              padding: '10px 16px', fontSize: 14, fontWeight: 600,
              cursor: folderBusy ? 'default' : 'pointer', fontFamily: 'inherit', letterSpacing: 0.2 }}>
            <Icon name="folder" size={15} /> New folder
          </button>
          {/* Not gated on sharePointAvailable: that flag is the folder-DISCOVERY
              result, and it reads false while the list is loading or when one
              Graph walk fails, which disabled the button on deals where creating
              a file works fine. The create route answers in words when
              SharePoint genuinely is not configured. */}
          <button onClick={() => setShowNewFile(true)}
            title="Start a blank Word, Excel or PowerPoint file and open it in Office Online"
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: B.white,
              color: B.navy, border: `1px solid ${B.border}`, borderRadius: 8,
              padding: '10px 16px', fontSize: 14, fontWeight: 600,
              cursor: 'pointer', fontFamily: 'inherit', letterSpacing: 0.2 }}>
            <Icon name="file" size={15} /> New file
          </button>
          <button onClick={openUpload}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: B.navy, color: '#fff', border: 'none', borderRadius: 8,
              padding: '10px 18px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
              fontFamily: 'inherit', letterSpacing: 0.2 }}>
            <Icon name="upload" size={15} /> Upload Document
          </button>
        </div>
      </div>

      {/* ── Search bar ── */}
      <div style={{ background: B.white, borderRadius: 10, padding: '14px 18px',
        marginBottom: 12, boxShadow: B.shadowMd }}>
        <div style={{ position: 'relative' }}>
          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)',
            display: 'flex', color: B.textLt, pointerEvents: 'none' }}><Icon name="search" size={15} /></span>
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search documents..."
            style={{ width: '100%', padding: '9px 14px 9px 34px', border: `1px solid ${B.border}`,
              borderRadius: 8, fontSize: 14, fontFamily: 'inherit', color: B.text,
              outline: 'none', boxSizing: 'border-box', background: B.surfaceMuted }}
          />
          {search && (
            <button onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
                background: 'none', border: 'none', cursor: 'pointer', fontSize: 16, color: B.textLt, lineHeight: 1 }}>
              ×
            </button>
          )}
        </div>
      </div>

      {/* ── Browser card ── */}
      <div style={{ background: B.white, borderRadius: 10, overflow: 'hidden', boxShadow: B.shadowMd }}>

        {/* Breadcrumb + item count bar */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '11px 20px', borderBottom: `1px solid ${B.border}`, background: B.white }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, flexWrap: 'wrap' }}>
            <span
              onClick={() => { setNavPath(''); setSearch(''); }}
              style={{ color: navPath || search ? B.navy : B.text, cursor: navPath || search ? 'pointer' : 'default',
                fontWeight: navPath || search ? 500 : 600, textDecoration: navPath || search ? 'underline' : 'none' }}>
              Documents
            </span>
            {!search && crumbs.map((crumb, i) => {
              const pathTo = crumbs.slice(0, i + 1).join('/');
              const isLast = i === crumbs.length - 1;
              return (
                <span key={pathTo} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                  <span style={{ color: B.textLt, fontSize: 11 }}>›</span>
                  <span
                    onClick={() => !isLast && setNavPath(pathTo)}
                    style={{ color: isLast ? B.text : B.navy, fontWeight: isLast ? 600 : 500,
                      cursor: isLast ? 'default' : 'pointer',
                      textDecoration: isLast ? 'none' : 'underline' }}>
                    {crumb}
                  </span>
                </span>
              );
            })}
            {search && (
              <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                <span style={{ color: B.textLt, fontSize: 11 }}>›</span>
                <span style={{ color: B.text, fontWeight: 600 }}>Search results</span>
              </span>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0, marginLeft: 12 }}>
            <div style={{ fontSize: 12, color: B.textLt }}>
              {search
                ? `${searchResults.length} result${searchResults.length !== 1 ? 's' : ''}`
                : `${totalVisible} item${totalVisible !== 1 ? 's' : ''}`}
            </div>
            {/* Folders can change in SharePoint without Harvi hearing about it —
                this re-reads past the server's short discovery cache. */}
            <button onClick={refreshFolders} disabled={foldersLoading}
              title="Re-read the folder list from SharePoint"
              style={{ background: 'none', border: 'none', cursor: foldersLoading ? 'default' : 'pointer',
                display: 'inline-flex', padding: 2, color: B.textLt }}>
              <Icon name="refresh" size={13} />
            </button>
          </div>
        </div>

        {/* Column headers */}
        <div style={{ display: 'grid', gridTemplateColumns: COL, padding: '9px 20px',
          borderBottom: `1px solid ${B.border}`, background: B.surfaceMuted, gap: 8 }}>
          <TH>NAME</TH>
          <TH>MODIFIED</TH>
          <TH>MODIFIED BY</TH>
          <TH>SIZE</TH>
          <TH>STATUS</TH>
          <TH />
        </div>

        {/* ── Search results (flat list) ── */}
        {search && (
          searchResults.length === 0
            ? <div style={{ padding: '48px 20px', textAlign: 'center', fontSize: 14, color: B.textLt }}>No documents found.</div>
            : searchResults.map((doc, i) => <FileRow key={doc.documentID} doc={doc} idx={i} total={searchResults.length} />)
        )}

        {/* ── Folder browser ── */}
        {!search && (
          <>
            {/* New-folder row — created in SharePoint too */}
            {newFolderName !== null && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 20px',
                borderBottom: `1px solid ${B.border}`, background: B.surfaceMuted }}>
                <span style={{ display: 'flex', color: B.tealDk, flexShrink: 0 }}><Icon name="folder" size={19} /></span>
                <input autoFocus value={newFolderName} disabled={folderBusy}
                  onChange={e => setNewFolderName(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') submitNewFolder(); if (e.key === 'Escape') setNewFolderName(null); }}
                  placeholder={navPath ? `New folder in ${navPath}` : 'New folder name'}
                  style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${B.border}`,
                    fontSize: 13, fontFamily: 'inherit', background: B.white, color: B.text, outline: 'none' }} />
                <button onClick={submitNewFolder} disabled={folderBusy || !newFolderName.trim()}
                  style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: B.navy, color: '#fff',
                    fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: folderBusy || !newFolderName.trim() ? 0.6 : 1 }}>
                  {folderBusy ? 'Creating…' : 'Create'}
                </button>
                <button onClick={() => setNewFolderName(null)} disabled={folderBusy}
                  style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${B.border}`, background: B.white,
                    color: B.textMd, fontSize: 12, cursor: 'pointer' }}>
                  Cancel
                </button>
              </div>
            )}

            {/* Folder rows */}
            {childFolders.map((folderName) => {
              const fullPath  = navPath ? `${navPath}/${folderName}` : folderName;
              const count     = countUnder(docs, fullPath);
              const isHov     = hovered === `folder:${fullPath}`;
              const isRenaming = renamingFolder?.path === fullPath;

              if (isRenaming) return (
                <div key={fullPath} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '11px 20px',
                  borderBottom: `1px solid ${B.border}`, background: B.surfaceMuted }}>
                  <span style={{ display: 'flex', color: B.tealDk, flexShrink: 0 }}><Icon name="folder" size={19} /></span>
                  <input autoFocus value={renamingFolder.value} disabled={folderBusy}
                    onChange={e => setRenamingFolder({ path: fullPath, value: e.target.value })}
                    onKeyDown={e => { if (e.key === 'Enter') submitRename(); if (e.key === 'Escape') setRenamingFolder(null); }}
                    style={{ flex: 1, padding: '6px 10px', borderRadius: 6, border: `1px solid ${B.border}`,
                      fontSize: 13, fontFamily: 'inherit', background: B.white, color: B.text, outline: 'none' }} />
                  <button onClick={submitRename} disabled={folderBusy}
                    style={{ padding: '6px 12px', borderRadius: 6, border: 'none', background: B.navy, color: '#fff',
                      fontSize: 12, fontWeight: 600, cursor: 'pointer', opacity: folderBusy ? 0.6 : 1 }}>
                    {folderBusy ? 'Renaming…' : 'Rename'}
                  </button>
                  <button onClick={() => setRenamingFolder(null)} disabled={folderBusy}
                    style={{ padding: '6px 10px', borderRadius: 6, border: `1px solid ${B.border}`, background: B.white,
                      color: B.textMd, fontSize: 12, cursor: 'pointer' }}>
                    Cancel
                  </button>
                </div>
              );

              return (
                <div key={fullPath}
                  onClick={() => setNavPath(fullPath)}
                  onMouseEnter={() => setHovered(`folder:${fullPath}`)}
                  onMouseLeave={() => setHovered(null)}
                  style={{ display: 'grid', gridTemplateColumns: COL, padding: '11px 20px', gap: 8,
                    borderBottom: `1px solid ${B.border}`, alignItems: 'center', cursor: 'pointer',
                    background: isHov ? B.surfaceMuted : B.white, transition: 'background 0.1s' }}>

                  {/* Name */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ display: 'flex', color: B.tealDk, flexShrink: 0 }}><Icon name="folder" size={19} /></span>
                    <span style={{ fontSize: 13, fontWeight: 600, color: B.text }}>{folderName}</span>
                  </div>

                  {/* Modified — blank for folders */}
                  <div />

                  {/* By — blank */}
                  <div />

                  {/* Size → item count */}
                  <div style={{ fontSize: 12, color: B.textMd }}>{count} item{count !== 1 ? 's' : ''}</div>

                  {/* Status — blank */}
                  <div />

                  {/* Rename / remove / chevron */}
                  <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 2 }}>
                    {isHov && (
                      <>
                        <button title="Rename folder (in SharePoint too)" disabled={folderBusy}
                          onClick={e => { e.stopPropagation(); setRenamingFolder({ path: fullPath, value: folderName }); }}
                          style={{ background: 'none', border: 'none', cursor: 'pointer', display: 'inline-flex',
                            padding: '4px', color: B.textMd }}>
                          <Icon name="edit" size={14} />
                        </button>
                        <button title={count ? 'Folder must be empty to remove' : 'Remove folder (in SharePoint too)'}
                          disabled={folderBusy || count > 0}
                          onClick={e => { e.stopPropagation(); removeFolder(fullPath); }}
                          style={{ background: 'none', border: 'none', cursor: count ? 'default' : 'pointer',
                            display: 'inline-flex', padding: '4px', color: B.red, opacity: count ? 0.25 : 0.65 }}>
                          <Icon name="trash" size={14} />
                        </button>
                      </>
                    )}
                    <span style={{ fontSize: 16, color: B.textLt }}>›</span>
                  </div>
                </div>
              );
            })}

            {/* File rows */}
            {childFiles.map((doc, i) => (
              <FileRow key={doc.documentID} doc={doc} idx={i} total={childFiles.length} />
            ))}

            {/* Empty state */}
            {totalVisible === 0 && (
              <div style={{ padding: '48px 20px', textAlign: 'center', fontSize: 14, color: B.textLt }}>
                This folder is empty.
              </div>
            )}
          </>
        )}
      </div>

      {/* ── Folder structure (AI) ── */}
      {showPlan && (
        <FolderPlanModal
          dealName={folderPlan.data?.dealName}
          plan={folderPlan.data}
          generating={folderPlan.generating}
          applying={folderPlan.applying}
          error={folderPlan.error}
          errorIsTimeout={folderPlan.errorIsTimeout}
          onGenerate={folderPlan.generate}
          onApply={async (opts) => {
            const res = await folderPlan.apply(opts);
            // Folders and documents both moved — the browser and the picker are
            // now looking at a stale tree.
            await refreshFolders();
            if (onRefresh) await onRefresh();
            return res;
          }}
          onClose={() => setShowPlan(false)}
        />
      )}

      {/* ── Move one document ── */}
      {moveDoc && (
        <Modal title={`Move "${moveDoc.doc.fileName}"`} width={500} onClose={() => !moving && setMoveDoc(null)}>
          <FolderPicker
            label="Move to folder"
            hint="Moving a file the AI filed teaches it where files like this belong."
            tree={folderTree}
            folders={liveFolders}
            loading={foldersLoading}
            error={foldersError}
            selected={moveDoc.target}
            onSelect={p => setMoveDoc(m => ({ ...m, target: p }))}
            onCreateFolder={createFolder}
            sharePointAvailable={sharePointAvailable}
          />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 16 }}>
            <button onClick={() => setMoveDoc(null)} disabled={moving}
              style={{ background: 'none', border: `1px solid ${B.border}`, borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit' }}>Cancel</button>
            <button onClick={confirmMove} disabled={moving || (moveDoc.target || '') === (moveDoc.doc.folderPath || '')}
              style={{ background: B.navy, color: B.white, border: 'none', borderRadius: 8, padding: '6px 14px', cursor: 'pointer', fontFamily: 'inherit' }}>
              {moving ? 'Moving…' : 'Move'}
            </button>
          </div>
        </Modal>
      )}

      {showNewFile && (
        <NewOfficeFileModal
          dealId={dealId}
          dealDetailId={dealDetailId}
          initialFolder={navPath}
          folderTree={folderTree}
          liveFolders={liveFolders}
          foldersLoading={foldersLoading}
          foldersError={foldersError}
          sharePointAvailable={sharePointAvailable}
          onClose={() => setShowNewFile(false)}
          onCreated={async (res) => {
            toast.success(`Created ${res?.fileName || 'the file'}${res?.webUrl ? ' — opened in a new tab' : ''}`);
            if (onRefresh) await onRefresh();
          }}
        />
      )}

      {/* ── Upload modal ── */}
      {showUpload && (
        <Modal title="Upload Document" width={500} onClose={closeUpload}>
            <FolderPicker
              label="Folder (optional)"
              hint="Leave this alone and each file is filed for you — pick one only to override that."
              tree={folderTree}
              folders={liveFolders}
              loading={foldersLoading}
              error={foldersError}
              selected={folderPath}
              onSelect={p => { setFolderPath(p); setUploadErr(null); }}
              taxYear={taxYear}
              onTaxYear={setTaxYear}
              onCreateFolder={createFolder}
              sharePointAvailable={sharePointAvailable}
            />

            <div
              onDragOver={e => { e.preventDefault(); if (!uploading) setDragging(true); }}
              onDragLeave={() => setDragging(false)}
              onDrop={async e => { e.preventDefault(); setDragging(false); if (!uploading) addEntries(await collectEntriesFromDataTransfer(e.dataTransfer)); }}
              onClick={() => !uploading && fileRef.current.click()}
              style={{ border: `2px dashed ${dragging ? B.navy : B.border}`, borderRadius: radius.lg,
                padding: '24px 16px', textAlign: 'center',
                background: dragging ? B.surfaceMuted : B.surface,
                cursor: uploading ? 'wait' : 'pointer', marginTop: 4, transition: 'all 0.15s' }}>
              <input ref={fileRef} type="file" multiple style={{ display: 'none' }}
                onChange={e => { addEntries(collectEntriesFromInputFiles(e.target.files)); e.target.value = ''; }} />
              <input ref={folderRef} type="file" webkitdirectory="" style={{ display: 'none' }}
                onChange={e => { addEntries(collectEntriesFromInputFiles(e.target.files)); e.target.value = ''; }} />
              <div style={{ display: 'flex', justifyContent: 'center', color: B.tealDk, marginBottom: 6 }}><Icon name="upload" size={22} /></div>
              <div style={{ fontSize: 13, fontWeight: 600, color: B.text }}>
                Drag &amp; drop files and folders here
              </div>
              <div style={{ fontSize: 11, color: B.textLt, marginTop: 3, display: 'flex', justifyContent: 'center', gap: 6, flexWrap: 'wrap' }}>
                <span onClick={e => { e.stopPropagation(); if (!uploading) fileRef.current.click(); }}
                  style={{ textDecoration: 'underline', cursor: uploading ? 'default' : 'pointer' }}>
                  Add files
                </span>
                <span style={{ color: B.border }}>·</span>
                <span onClick={e => { e.stopPropagation(); if (!uploading) folderRef.current.click(); }}
                  style={{ textDecoration: 'underline', cursor: uploading ? 'default' : 'pointer' }}>
                  Add a folder
                </span>
                <span style={{ color: B.border }}>·</span>
                <span>any file type · keep adding to build one upload</span>
              </div>
            </div>

            {/* ── Staged files list ── */}
            {staged.length > 0 && (() => {
              const folderCount = new Set(staged.map(e => e.relativePath.split('/')[0]).filter(Boolean)).size;
              const fileCount   = staged.length;
              return (
                <div style={{ marginTop: 12, border: `1px solid ${B.border}`, borderRadius: radius.md, overflow: 'hidden' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    padding: '8px 12px', background: B.surfaceMuted, borderBottom: `1px solid ${B.border}` }}>
                    <span style={{ fontSize: 12, fontWeight: 700, color: B.textMd, letterSpacing: 0.3 }}>
                      {fileCount} file{fileCount !== 1 ? 's' : ''}{folderCount ? ` · ${folderCount} folder${folderCount !== 1 ? 's' : ''}` : ''} ready
                    </span>
                    {!uploading && (
                      <button onClick={() => setStaged([])}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: B.textLt, fontFamily: 'inherit' }}>
                        Clear all
                      </button>
                    )}
                  </div>
                  <div style={{ maxHeight: 200, overflowY: 'auto' }}>
                    {staged.map((e, i) => (
                      <div key={e.key}
                        style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
                          borderBottom: i < staged.length - 1 ? `1px solid ${B.border}` : 'none' }}>
                        <FileChip fileName={e.file.name} />
                        <div style={{ minWidth: 0, flex: 1 }}>
                          <div style={{ fontSize: 12, fontWeight: 500, color: B.text,
                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.file.name}</div>
                          {e.relativePath && (
                            <div style={{ fontSize: 10, color: B.textLt, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <Icon name="folder" size={10} /> {e.relativePath}
                            </div>
                          )}
                        </div>
                        <span style={{ fontSize: 11, color: B.textLt, flexShrink: 0 }}>{e.file.size ? fmt.fileSize(e.file.size) : ''}</span>
                        {!uploading && (
                          <button onClick={() => removeStaged(e.key)} title="Remove"
                            style={{ background: 'none', border: 'none', cursor: 'pointer', color: B.textLt, display: 'inline-flex', padding: 2, flexShrink: 0 }}>
                            <Icon name="close" size={13} />
                          </button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              );
            })()}

            {uploadErr && <div style={{ color: B.red, fontSize: 12, marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 4 }}><Icon name="warning" size={13} /> {uploadErr}</div>}

            {/* ── Footer actions ── */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 10, marginTop: 16 }}>
              {uploading && uploadProgress && (
                <span style={{ fontSize: 12, color: B.textMd, marginRight: 'auto' }}>
                  Uploading… {uploadProgress.done} / {uploadProgress.total}
                </span>
              )}
              <button onClick={closeUpload} disabled={uploading}
                style={{ background: 'none', border: `1px solid ${B.border}`, borderRadius: 8, padding: '9px 16px',
                  fontSize: 14, fontWeight: 600, color: B.textMd, cursor: uploading ? 'default' : 'pointer', fontFamily: 'inherit' }}>
                Cancel
              </button>
              <button onClick={uploadStaged} disabled={uploading || staged.length === 0}
                style={{ background: (uploading || staged.length === 0) ? B.border : B.navy, color: '#fff', border: 'none', borderRadius: 8,
                  padding: '9px 18px', fontSize: 14, fontWeight: 600, cursor: (uploading || staged.length === 0) ? 'default' : 'pointer',
                  fontFamily: 'inherit', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                <Icon name="upload" size={15} />
                {uploading ? 'Uploading…' : staged.length > 0 ? `Upload ${staged.length} file${staged.length !== 1 ? 's' : ''}` : 'Upload'}
              </button>
            </div>
        </Modal>
      )}

      <DocViewerModal viewerState={viewerState} onClose={() => setViewerState(null)} />

      {/* ── Document-type editor ── */}
      {tagDocId && (
        <DocumentTagModal
          doc={docs.find(d => d.documentID === tagDocId) || null}
          onApply={applyType}
          onRemove={removeType}
          onClose={() => setTagDocId(null)}
          onOpenSplit={(d) => { setTagDocId(null); setSplitDocId(d.documentID); }}
        />
      )}

      {/* ── Split review ── */}
      {splitDocId && (
        <SplitDocumentModal
          doc={docs.find(d => d.documentID === splitDocId) || null}
          onClose={() => setSplitDocId(null)}
          onDone={onRefresh}
        />
      )}
    </div>
  );
};
